import { omit } from 'lodash'
import React, { useContext, useState, useEffect } from 'react'
import { t } from '@lingui/macro'
import queryString from 'querystring'
import { Location } from 'history'
import { useLocation } from 'react-router-dom'

import { linkBuilder } from '../../../../../../shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import { notification } from '../../../../components/common/notifications/Notifications'

import { translateActionType } from '../../campaign-overview/create-campaign-action-modal/CreateCampaignActionModal'
import { ManageCampaignContext } from '../../ManageCampaignPage'
import {
  CampaignActionPathwayModel,
  CampaignActionSurveyModel,
  CampaignActionTopicModel,
  CampaignCertificationShareActionModelAsEditor,
  CampaignPathwayShareActionModel,
  CampaignSurveyShareActionModel,
  CampaignTopicShareActionModel,
  CampaignScheduleEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import './CampaignShareComponent.less'
import CampaignShareStepSettings from './CampaignShareStepSettings'
import { CampaignActionScheduleStepComponent } from '../campaign-action-schedule-step-component/CampaignActionScheduleStepComponent'

import {
  useUpdateCertificationShareActionMutation,
  useCreateCertificationShareActionMutation,
  useCreateTopicShareActionMutation,
  useCreatePathwayShareActionMutation,
  useCreateSurveyShareActionMutation,
  useUpdatePathwayShareActionMutation,
  useUpdateSurveyShareActionMutation,
  useUpdateTopicShareActionMutation
} from './CampaignShareComponent.codegen'
import { useDefaultSender } from '../SenderSelect'
import dayjs from 'shared/dayjs'
import { Dayjs } from 'dayjs'
import { getUTCDateFromTimeZoneDate } from '../../../manage-events/event-edit/tabs/event-detail/eventDetailUtils'
import { checkDayJsAndFormat } from '../../../../components/common/date-time-pickers/dateTimePickersUtils'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export interface ICampaignShareComponentProps {
  dataQuery?: any
  dataPath?: string
  title: string
  itemList?: JSX.Element
  type: CampaignActionTypeEnum
  routeLocation?: Location
  shareId?: number
}

export interface ICampaignShareComponentState {
  step: string
  message: string
  toggleCompletionDate: boolean
  sendShareEmail: boolean
  completionDate: Dayjs | null | string
  relativeCompletion: {
    day: number | null
    time: string | null
  }
  resetEnrolments: boolean
  timezone: string | null
  scheduleType: CampaignScheduleEnum
  relativeSchedule: {
    day: number | null
    time: string | null
  }
  fixedSchedule: { date: Dayjs | null; valid?: boolean }
  automatic_time_of_day: boolean
  isSaving: boolean
  email_identity_id: number | null
  respect_business_hours: boolean
}

export const STEPS = {
  SELECT: 'Select',
  SETTINGS: 'Settings',
  SCHEDULE: 'Schedule'
}

const CampaignShareComponent = (props: ICampaignShareComponentProps) => {
  const { campaign } = useContext(ManageCampaignContext)
  const loc = useLocation()
  const [updateTopicShareAction] = useUpdateTopicShareActionMutation()
  const [createTopicShareAction] = useCreateTopicShareActionMutation()
  const [updatePathwayShareAction] = useUpdatePathwayShareActionMutation()
  const [createPathwayShareAction] = useCreatePathwayShareActionMutation()
  const [updateSurveyShareAction] = useUpdateSurveyShareActionMutation()
  const [createSurveyShareAction] = useCreateSurveyShareActionMutation()
  const [updateCertificationShareAction] =
    useUpdateCertificationShareActionMutation()
  const [createCertificationShareAction] =
    useCreateCertificationShareActionMutation()
  const defaultSender = useDefaultSender(campaign.id)

  let selectedItemsIds: number[] = []
  let shareData: any = {}
  if (!props.shareId) {
    // New share form
    selectedItemsIds = getSelectedItemsFromUrl(loc)
    if (selectedItemsIds.length === 0) {
      navigateTo(linkBuilder.campaign(campaign.id))
    }
  } else {
    // Edit share form
    selectedItemsIds = getSelectedItemsFromCampaign(props, campaign)
    shareData = getShareActionData(props, campaign)
  }

  const [state, setState] = useState<ICampaignShareComponentState>({
    step: STEPS.SETTINGS,
    message: shareData.text || '',
    toggleCompletionDate:
      !!shareData.target_completion_date ||
      !!shareData.relative_target_completion_day ||
      false,
    sendShareEmail:
      typeof shareData.send_share_email === 'boolean'
        ? shareData.send_share_email
        : false,
    completionDate: shareData.target_completion_date || null,
    relativeCompletion: {
      day:
        'relative_target_completion_day' in shareData
          ? shareData.relative_target_completion_day
          : null,
      time:
        'relative_target_completion_time' in shareData
          ? shareData.relative_target_completion_time
          : null
    },
    resetEnrolments:
      typeof shareData.reset_existing_enrolments === 'boolean'
        ? shareData.reset_existing_enrolments
        : false,
    timezone: dayjs.tz.guess(),
    scheduleType: campaign.schedule_type as CampaignScheduleEnum,
    relativeSchedule: {
      day: 'send_at_day' in shareData ? shareData.send_at_day : 1,
      time: 'send_at_time' in shareData ? shareData.send_at_time : '09:00:00'
    },
    fixedSchedule: {
      date: shareData.fixed_send_time ? dayjs(shareData.fixed_send_time) : null
    },
    automatic_time_of_day: shareData.automatic_time_of_day,
    respect_business_hours: shareData.respect_business_hours,
    isSaving: false,
    email_identity_id: shareData.email_identity_id
  })

  useEffect(() => {
    // prefill default sender for new shares
    if (!props.shareId) {
      setState((s) => ({ ...s, email_identity_id: defaultSender }))
    }
  }, [defaultSender])

  const createOrUpdateCampaignAction = async (
    createActionFunction,
    updateActionFunction
  ) => {
    if (props.shareId) {
      await createActionFunction({
        variables: {
          id: props.shareId,
          input: prepareDataToSave(),
          campaignId: campaign.id
        }
      })
    } else {
      await updateActionFunction({
        variables: {
          id: campaign.id,
          input: prepareDataToSave()
        }
      })
    }
  }

  const prepareDataToSave = () => {
    let data: any = {
      text: state.message,
      target_completion_date: null,
      reset_existing_enrolments: state.resetEnrolments,
      send_share_email: state.sendShareEmail,
      relative_target_completion_day: null,
      relative_target_completion_time: null,
      send_at_day: null,
      send_at_time: null,
      timezone: null,
      fixed_send_time: null,
      automatic_time_of_day: state.automatic_time_of_day,
      respect_business_hours: state.respect_business_hours,
      email_identity_id: state.email_identity_id
    }

    const isCompletionDateEnable = state.toggleCompletionDate === true

    if (state.scheduleType === CampaignScheduleEnum.RELATIVE) {
      data.relative_target_completion_day = isCompletionDateEnable
        ? state.relativeCompletion.day
        : null
      data.relative_target_completion_time = isCompletionDateEnable
        ? state.relativeCompletion.time
        : null
      data.send_at_day = state.relativeSchedule.day
      data.send_at_time = state.relativeSchedule.time
    } else if (state.scheduleType === CampaignScheduleEnum.FIXED) {
      const timezone = state.timezone || dayjs.tz.guess()

      data.target_completion_date = isCompletionDateEnable
        ? checkDayJsAndFormat(state.completionDate)
        : null
      data.fixed_send_time = getUTCDateFromTimeZoneDate({
        date: state.fixedSchedule?.date,
        timezone
      })

      data.timezone = timezone
    } else if (state.scheduleType === CampaignScheduleEnum.IMMEDIATE) {
      data.target_completion_date = isCompletionDateEnable
        ? checkDayJsAndFormat(state.completionDate)
        : null
    }

    switch (props.type) {
      case CampaignActionTypeEnum.TOPIC_SHARE_ACTION:
        data.actionTopics = selectedItemsIds
        break
      case CampaignActionTypeEnum.PATHWAY_SHARE_ACTION:
        data.actionPathways = selectedItemsIds
        break
      case CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION:
        data.actionCertifications = selectedItemsIds
        break
      case CampaignActionTypeEnum.SURVEY_SHARE_ACTION:
        data = omit(data, [
          'target_completion_date',
          'reset_existing_enrolments'
        ])
        data.actionSurveys = selectedItemsIds
        break
      default:
    }

    return data
  }

  const onSave = async () => {
    try {
      updateState({ isSaving: true })
      switch (props.type) {
        case CampaignActionTypeEnum.TOPIC_SHARE_ACTION:
          await createOrUpdateCampaignAction(
            updateTopicShareAction,
            createTopicShareAction
          )
          break
        case CampaignActionTypeEnum.PATHWAY_SHARE_ACTION:
          await createOrUpdateCampaignAction(
            updatePathwayShareAction,
            createPathwayShareAction
          )
          break
        case CampaignActionTypeEnum.SURVEY_SHARE_ACTION:
          await createOrUpdateCampaignAction(
            updateSurveyShareAction,
            createSurveyShareAction
          )
          break
        case CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION:
          await createOrUpdateCampaignAction(
            updateCertificationShareAction,
            createCertificationShareAction
          )
          break
        default:
      }

      notification.info(
        props.shareId
          ? t`Campaign ${translateActionType(
              props.type
            )} action has been updated`
          : t`Campaign ${translateActionType(
              props.type
            )} action has been created`
      )
    } catch (err) {
      updateState({ isSaving: false })
      notification.error(
        props.shareId
          ? t`Error updating ${translateActionType(props.type)} action`
          : t`Error creating ${translateActionType(props.type)} action`
      )
      throw err
    } finally {
      updateState({ isSaving: false })
    }

    navigateTo(linkBuilder.campaign(campaign.id))
  }

  const updateState = (updatedState: Partial<ICampaignShareComponentState>) => {
    setState({ ...state, ...updatedState })
  }

  return (
    <div className="CampaignShareComponent">
      {state.step === STEPS.SETTINGS && (
        <CampaignShareStepSettings
          {...state}
          {...props}
          onStateUpdate={updateState}
          selectedItemsIds={selectedItemsIds}
        />
      )}
      {state.step === STEPS.SCHEDULE && (
        <CampaignActionScheduleStepComponent
          {...state}
          onStateUpdate={updateState}
          onSave={onSave}
          actionType={props.type}
          steps={STEPS}
        />
      )}
    </div>
  )
}

const getSelectedItemsFromUrl = (routeLocation?: Location) => {
  const queryPart = routeLocation?.search.replace('?', '')

  if (!queryPart) {
    return []
  }
  const queryParsed = queryString.parse(queryPart)
  if (queryParsed.selectedIds) {
    const { selectedIds } = queryParsed
    if (Array.isArray(selectedIds)) {
      return selectedIds.map((id) => +id)
    }
    return [+selectedIds]
  }
  return []
}

const getSelectedItemsFromCampaign = (
  props: ICampaignShareComponentProps,
  campaign
) => {
  const shareActionData = getShareActionData(props, campaign)
  switch (props.type) {
    case CampaignActionTypeEnum.TOPIC_SHARE_ACTION:
      return (
        shareActionData as CampaignTopicShareActionModel
      ).actionTopics.map(
        (actionTopic: CampaignActionTopicModel) => actionTopic.topic.id
      )
    case CampaignActionTypeEnum.PATHWAY_SHARE_ACTION:
      return (
        shareActionData as CampaignPathwayShareActionModel
      ).actionPathways.map(
        (actionPathway: CampaignActionPathwayModel) => actionPathway.pathway.id
      )
    case CampaignActionTypeEnum.SURVEY_SHARE_ACTION:
      return (
        shareActionData as CampaignSurveyShareActionModel
      ).actionSurveys.map(
        (actionSurvey: CampaignActionSurveyModel) => actionSurvey.survey.id
      )
    case CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION:
      return (
        shareActionData as CampaignCertificationShareActionModelAsEditor
      ).actionCertifications.map(
        (actionCertification) => actionCertification.certification.id
      )
    default:
      throw new Error(`Action type ${props.type} is not implemented`)
  }
}

const getShareActionData = (
  props: ICampaignShareComponentProps,
  campaign
):
  | CampaignTopicShareActionModel
  | CampaignPathwayShareActionModel
  | CampaignSurveyShareActionModel
  | CampaignCertificationShareActionModelAsEditor
  | null => {
  switch (props.type) {
    case CampaignActionTypeEnum.TOPIC_SHARE_ACTION:
      return campaign.topicShareActions.find(
        (topicShare: CampaignTopicShareActionModel) => {
          return topicShare.id === props.shareId
        }
      ) as CampaignTopicShareActionModel
    case CampaignActionTypeEnum.PATHWAY_SHARE_ACTION:
      return campaign.pathwayShareActions.find(
        (pathwayShare: CampaignPathwayShareActionModel) => {
          return pathwayShare.id === props.shareId
        }
      ) as CampaignPathwayShareActionModel
    case CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION:
      return campaign.certificationShareActions.find(
        (certificationShare: CampaignCertificationShareActionModelAsEditor) => {
          return certificationShare.id === props.shareId
        }
      ) as CampaignPathwayShareActionModel
    case CampaignActionTypeEnum.SURVEY_SHARE_ACTION:
      return campaign.surveyShareActions.find(
        (surveyShare: CampaignSurveyShareActionModel) => {
          return surveyShare.id === props.shareId
        }
      ) as CampaignSurveyShareActionModel
    default:
      return null
  }
}

export default CampaignShareComponent
